import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ListMusic, Send, Check, X, Loader2, Music, Save, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { useGuild } from '@/contexts/GuildContext';
import {
  API, authHeader, inputCls, labelCls, cardCls, btnPrimary, timeAgo, fmtDuration,
  Toggle, PageShell, PageLoading, EmptyState,
} from '@/lib/pageKit';

const STATUS_STYLES = {
  pending: 'bg-amber-500/10 border-amber-500/20 text-amber-400',
  approved: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
  denied: 'bg-red-500/10 border-red-500/20 text-red-400',
  failed: 'bg-zinc-500/10 border-zinc-500/20 text-[var(--text-muted)]',
};

function StatusPill({ status }) {
  return (
    <span className={`text-[11px] font-medium font-[Manrope] px-2 py-0.5 rounded-full border capitalize ${STATUS_STYLES[status] || STATUS_STYLES.failed}`}>
      {status}
    </span>
  );
}

function SettingsCard({ guildId, settings, onSaved }) {
  const [s, setS] = useState(settings);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setS(settings); }, [settings]);
  const save = async () => {
    setSaving(true);
    try {
      const body = { enabled: !!s.enabled, auto_approve: !!s.auto_approve, max_pending_per_user: Number(s.max_pending_per_user) || 0 };
      await axios.put(`${API}/api/song-requests/${guildId}/settings`, body, authHeader());
      toast.success('Song request settings saved');
      onSaved(body);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className={`${cardCls} p-5 space-y-4`}>
      <p className="text-[var(--text-primary)] text-sm font-semibold font-[Outfit] flex items-center gap-2"><Settings2 size={15} className="text-[#5865F2]" /> Request settings</p>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[var(--text-primary)] text-sm font-medium font-[Manrope]">Enable song requests</p>
          <p className="text-[var(--text-muted)] text-xs font-[Manrope]">Let members request songs from the dashboard</p>
        </div>
        <Toggle checked={!!s.enabled} onChange={v => setS(p => ({ ...p, enabled: v }))} />
      </div>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[var(--text-primary)] text-sm font-medium font-[Manrope]">Auto-approve</p>
          <p className="text-[var(--text-muted)] text-xs font-[Manrope]">Queue requests immediately without review</p>
        </div>
        <Toggle checked={!!s.auto_approve} onChange={v => setS(p => ({ ...p, auto_approve: v }))} />
      </div>
      <div className="max-w-[200px]">
        <label className={labelCls}>Max pending per user</label>
        <input type="number" min={1} value={s.max_pending_per_user ?? ''} onChange={e => setS(p => ({ ...p, max_pending_per_user: e.target.value === '' ? '' : parseInt(e.target.value, 10) }))} className={inputCls} />
      </div>
      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className={btnPrimary}>
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
      </div>
    </div>
  );
}

export default function SongRequests() {
  const { selectedGuild, selectedGuildId } = useGuild();
  const isGlobal = !!selectedGuild?.is_global;
  const isFull = !!(selectedGuild?.is_staff || selectedGuild?.is_mod || selectedGuild?.is_dj);
  const isStaff = !!selectedGuild?.is_staff;
  const [requests, setRequests] = useState([]);
  const [settings, setSettings] = useState({ enabled: true, auto_approve: false, max_pending_per_user: 3 });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [sending, setSending] = useState(false);
  const [acting, setActing] = useState(null);

  const load = useCallback(async () => {
    if (!selectedGuildId || isGlobal) { setLoading(false); return; }
    try {
      const r = await axios.get(`${API}/api/song-requests/${selectedGuildId}`, authHeader());
      setRequests(r.data.requests || []);
      if (r.data.settings) setSettings(r.data.settings);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load song requests');
    } finally {
      setLoading(false);
    }
  }, [selectedGuildId, isGlobal]);

  useEffect(() => {
    setLoading(true);
    load();
    if (!isFull) return undefined;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load, isFull]);

  const submit = async (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/api/song-requests`, { guild_id: selectedGuildId, query: q }, authHeader());
      toast.success(data.message || (data.status === 'queued' ? 'Song queued' : 'Request submitted'));
      setQuery('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSending(false);
    }
  };

  const act = async (id, action) => {
    setActing(`${id}:${action}`);
    try {
      await axios.post(`${API}/api/song-requests/${selectedGuildId}/${id}/${action}`, {}, authHeader());
      toast.success(action === 'approve' ? 'Request approved' : 'Request denied');
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} request`);
    } finally {
      setActing(null);
      load();
    }
  };

  if (loading) return <PageLoading />;

  return (
    <PageShell
      icon={ListMusic} title="Song Requests"
      subtitle={isFull ? 'Review and manage requests from members' : 'Request a song and track its status'}
      testId="song-requests-main" isGlobal={isGlobal}
    >
      <div className={`${cardCls} p-5`}>
        {settings.enabled === false ? (
          <p className="text-amber-400 text-sm font-[Manrope]">Song requests are currently disabled in this server.</p>
        ) : (
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
            <input
              value={query} onChange={e => setQuery(e.target.value)} disabled={sending}
              placeholder="Song name or YouTube / Spotify link" className={inputCls} data-testid="song-request-input"
            />
            <button type="submit" disabled={sending || !query.trim()} className={`${btnPrimary} justify-center flex-shrink-0`}>
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Request
            </button>
          </form>
        )}
        <p className="text-[var(--text-muted)] text-xs font-[Manrope] mt-2">
          {settings.auto_approve ? 'Requests are queued automatically.' : 'Requests are reviewed by a DJ or moderator before they are queued.'}
        </p>
      </div>

      {isStaff && <SettingsCard guildId={selectedGuildId} settings={settings} onSaved={setSettings} />}

      <div>
        <h2 className="text-[var(--text-primary)] font-semibold font-[Outfit] text-lg mb-3">{isFull ? 'All requests' : 'Your requests'}</h2>
        {requests.length === 0 ? (
          <EmptyState icon={ListMusic} title="No song requests yet" />
        ) : (
          <div className={`${cardCls} divide-y divide-[var(--border)] overflow-hidden`}>
            {requests.map(r => (
              <div key={r.id} className="flex items-center gap-4 px-5 py-3.5">
                {r.thumbnail ? (
                  <img src={r.thumbnail} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-[#5865F2]/15 flex items-center justify-center flex-shrink-0"><Music size={18} className="text-[#5865F2]" /></div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-[var(--text-primary)] text-sm font-medium font-[Manrope] truncate">{r.title || r.query}</p>
                  <p className="text-[var(--text-muted)] text-xs font-[Manrope] truncate">
                    {isFull && r.requested_by_name ? `${r.requested_by_name} · ` : ''}
                    {r.duration ? `${fmtDuration(r.duration)} · ` : ''}{timeAgo(r.created_at)}
                    {r.handled_by ? ` · by ${r.handled_by}` : ''}
                    {r.note ? ` · ${r.note}` : ''}
                  </p>
                </div>
                <StatusPill status={r.status} />
                {isFull && r.status === 'pending' && (
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button onClick={() => act(r.id, 'approve')} disabled={!!acting} title="Approve"
                      className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20 disabled:opacity-50 transition-colors">
                      {acting === `${r.id}:approve` ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                    </button>
                    <button onClick={() => act(r.id, 'deny')} disabled={!!acting} title="Deny"
                      className="p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 disabled:opacity-50 transition-colors">
                      {acting === `${r.id}:deny` ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </PageShell>
  );
}
